/**
 * DEV-024 · Shared presentation helper for the four half-day
 * availability states (Full / AM / PM / Off).
 *
 * ONE source of truth for:
 *   - the day-state palette (background / border / text tints)
 *   - short and long labels
 *   - per-state hours
 *   - deriving the pattern from a legacy `workingDays` array
 *   - the weekly-hours total for a pattern
 *
 * Used by both the Availability editor (`EngineerForm.jsx`) and the
 * read-only availability card (`EngineerAvailabilityView.jsx`) so
 * the card and editor can never diverge on colour, label or total.
 *
 * Presentation only. NO backend / API contract / stored-availability
 * changes.
 */

export const DAY_STATE_KEYS = ["full", "morning", "afternoon", "none"];

// Tone map — reused verbatim by the editor's cycling buttons and the
// read-only card cells. Any future adjustment lands here once.
export const DAY_STATE_STYLE = {
    full: {
        label:      "Full",
        longLabel:  "Full day",
        hours:      8,
        // Editor tone — solid Pearl green for the "on" state.
        editorCls:  "bg-[#3E9E2E] border-[#3E9E2E] text-white",
        // Card tone — same hue, lighter surface so the read-only cell
        // sits calmly inside the resource card.
        cardCls:    "bg-[#3E9E2E]/12 text-[#166534] border border-[#3E9E2E]/35",
        // Inline accent — used by the small state pill next to the
        // weekday label.
        accentCls:  "text-[#166534]",
    },
    morning: {
        label:      "AM",
        longLabel:  "Morning only",
        hours:      4,
        editorCls:  "bg-[#FEF3C7] border-[#F0B429] text-[#7A5B00]",
        cardCls:    "bg-[#FEF3C7] text-[#7A5B00] border border-[#F0B429]/60",
        accentCls:  "text-[#B45309]",
    },
    afternoon: {
        label:      "PM",
        longLabel:  "Afternoon only",
        hours:      4,
        editorCls:  "bg-[#DBEAFE] border-[#60A5FA] text-[#1E3A8A]",
        cardCls:    "bg-[#DBEAFE] text-[#1E3A8A] border border-[#60A5FA]/60",
        accentCls:  "text-[#1E3A8A]",
    },
    none: {
        label:      "Off",
        longLabel:  "Non-working",
        hours:      0,
        editorCls:  "bg-white border-[#E5E7EB] text-[#94A3B8]",
        cardCls:    "bg-[#F5F5F7] text-[#9CA3AF] border border-transparent",
        accentCls:  "text-[#94A3B8]",
    },
};

/**
 * Resolve the state a resource has for a given ISO weekday (1..7).
 *
 * Precedence:
 *   1. Explicit `workingDayPattern` entry (from the half-day editor).
 *   2. Legacy fallback: any day present in `workingDays` counts as
 *      Full; any day absent counts as Off. This is the compatibility
 *      handling DEV-024 requires so legacy rows keep displaying the
 *      correct Full/Off pattern before a user opens the editor.
 */
export function stateForDay(engineer, isoDay) {
    if (!engineer) return "none";
    const pattern = engineer.workingDayPattern;
    if (pattern && typeof pattern === "object") {
        const v = pattern[String(isoDay)];
        if (v && DAY_STATE_KEYS.includes(v)) return v;
    }
    const list = engineer.workingDays;
    if (Array.isArray(list) && list.includes(isoDay)) return "full";
    return "none";
}

/**
 * Weekly working-hours total for a resource, computed the SAME way
 * as the editor's live total — see `_weekly_hours_from_pattern` on
 * the backend for the mirroring implementation. Handles the legacy
 * ``workingDays``-only case so cards without a saved pattern still
 * show a truthful total.
 *
 * The total represents the NORMAL weekly working pattern only —
 * holidays, blackouts and appointments are NOT subtracted.
 */
export function weeklyHoursFor(engineer) {
    let total = 0;
    for (let d = 1; d <= 7; d++) {
        total += DAY_STATE_STYLE[stateForDay(engineer, d)].hours;
    }
    return total;
}
